import { useState, useEffect, useRef, useCallback } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/frontend/components/ui/dropdown-menu";
import { Button } from "@/frontend/components/ui/button";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { saleIncidentClassLabels } from "@/frontend/lib/sale-incident-labels";
import type {
  SaleIncident,
  IncidentStatus,
  SaleIncidentClass,
} from "@/frontend/lib/types";

interface SaleIncidentWithChatName extends SaleIncident {
  chatName: string | null;
}

interface SaleIncidentListProps {
  onIncidentSelect?: (
    incidentId: number,
    chatId: string,
    incidentDate: string
  ) => void;
  selectedIncidentId?: number;
  initialScrollToIncidentId?: number;
  viewMode?: "chats" | "incidents";
  onViewModeChange?: (mode: "chats" | "incidents") => void;
}

type SortOption = "newest" | "oldest";

function getRelativeTime(unixtime: number): string {
  const date = new Date(unixtime * 1000);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return "just now";
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays} ${diffInDays === 1 ? "day" : "days"} ago`;
  }

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) {
    return `${diffInWeeks} ${diffInWeeks === 1 ? "week" : "weeks"} ago`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  return `${diffInMonths} ${diffInMonths === 1 ? "month" : "months"} ago`;
}

function getClassBadgeColor(incidentClass: SaleIncidentClass): string {
  switch (incidentClass) {
    case "SLA_violation":
      return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300";
    case "toxic_client":
      return "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300";
    case "toxic_manager":
      return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300";
    case "negative_review_refusal":
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300";
    case "false_client_expectations":
      return "bg-lime-100 text-lime-800 dark:bg-lime-900/30 dark:text-lime-300";
    case "missing_diagnostics":
      return "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300";
    case "missing_next_step":
      return "bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300";
    case "poor_objection_handling":
      return "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300";
  }
}

function getIncidentBackgroundColor(status: IncidentStatus): string {
  switch (status) {
    case "open":
      return "bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-950/40";
    case "resolved":
      return "bg-green-50 dark:bg-green-950/30 hover:bg-green-100 dark:hover:bg-green-950/40";
    case "discarded":
      return "bg-gray-50 dark:bg-gray-800/30 hover:bg-gray-100 dark:hover:bg-gray-800/40";
    default:
      return "bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-950/40";
  }
}

function getStatusLabel(status: IncidentStatus): string {
  switch (status) {
    case "open":
      return "Open";
    case "resolved":
      return "Resolved";
    case "discarded":
      return "Discarded";
    default:
      return "Open";
  }
}

const allSaleIncidentClasses: SaleIncidentClass[] = [
  "missing_diagnostics",
  "missing_next_step",
  "toxic_manager",
  "toxic_client",
  "SLA_violation",
  "negative_review_refusal",
  "false_client_expectations",
  "poor_objection_handling",
];

const STORAGE_KEY_SORT = "saleIncidentList.sortBy";
const STORAGE_KEY_STATUS = "saleIncidentList.statusFilters";
const STORAGE_KEY_CLASS = "saleIncidentList.classFilters";

function loadFromStorage<T>(key: string, defaultValue: T): T {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch {
    return defaultValue;
  }
}

function saveToStorage(key: string, value: any): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Silent fail
  }
}

export function SaleIncidentList({
  onIncidentSelect,
  selectedIncidentId,
  initialScrollToIncidentId,
  viewMode,
  onViewModeChange,
}: SaleIncidentListProps) {
  const [incidents, setIncidents] = useState<SaleIncidentWithChatName[]>([]);
  const [sortBy, setSortBy] = useState<SortOption>(() =>
    loadFromStorage(STORAGE_KEY_SORT, "newest")
  );
  const [statusFilters, setStatusFilters] = useState<Set<IncidentStatus>>(
    () => {
      const saved = loadFromStorage<IncidentStatus[]>(STORAGE_KEY_STATUS, [
        "open",
      ]);
      return new Set(saved);
    }
  );
  const [classFilters, setClassFilters] = useState<Set<SaleIncidentClass>>(() => {
    const saved = loadFromStorage<SaleIncidentClass[]>(
      STORAGE_KEY_CLASS,
      allSaleIncidentClasses
    );
    return new Set(saved);
  });
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [, setTick] = useState(0);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const observerTarget = useRef<HTMLDivElement>(null);
  const incidentRefs = useRef<Map<number, HTMLButtonElement>>(new Map());
  const hasScrolledToInitialIncident = useRef(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setTick((t) => t + 1);
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    saveToStorage(STORAGE_KEY_SORT, sortBy);
  }, [sortBy]);

  useEffect(() => {
    saveToStorage(STORAGE_KEY_STATUS, Array.from(statusFilters));
  }, [statusFilters]);

  useEffect(() => {
    saveToStorage(STORAGE_KEY_CLASS, Array.from(classFilters));
  }, [classFilters]);

  const fetchIncidents = useCallback(
    async (offset: number = 0, reset: boolean = false) => {
      if (isLoading) return;

      setIsLoading(true);
      try {
        const params = new URLSearchParams({
          offset: offset.toString(),
          limit: "15",
          sortOrder: sortBy === "newest" ? "desc" : "asc",
        });

        if (statusFilters.size > 0) {
          params.append("status", Array.from(statusFilters).join(","));
        }

        if (
          classFilters.size > 0 &&
          classFilters.size < allSaleIncidentClasses.length
        ) {
          params.append("class", Array.from(classFilters).join(","));
        }

        const response = await fetch(`/api/sale/incidents?${params.toString()}`);
        const data = await response.json();

        setIncidents((prev) =>
          reset ? data.incidents : [...prev, ...data.incidents]
        );
        setHasMore(data.hasMore);
      } catch (error) {
        console.error("Error fetching sale incidents:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [sortBy, statusFilters, classFilters, isLoading]
  );

  useEffect(() => {
    setIncidents([]);
    setHasMore(true);
    fetchIncidents(0, true);
  }, [sortBy, statusFilters, classFilters]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !isLoading) {
          fetchIncidents(incidents.length, false);
        }
      },
      { threshold: 1.0 }
    );

    const target = observerTarget.current;
    if (target) {
      observer.observe(target);
    }

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [incidents.length, hasMore, isLoading, fetchIncidents]);

  useEffect(() => {
    if (
      initialScrollToIncidentId &&
      incidents.length > 0 &&
      !isLoading &&
      !hasScrolledToInitialIncident.current
    ) {
      const incidentElement = incidentRefs.current.get(
        initialScrollToIncidentId
      );
      if (incidentElement && scrollContainerRef.current) {
        incidentElement.scrollIntoView({ behavior: "smooth", block: "center" });
        hasScrolledToInitialIncident.current = true;
      }
    }
  }, [initialScrollToIncidentId, incidents, isLoading]);

  const toggleStatusFilter = (status: IncidentStatus) => {
    setStatusFilters((prev) => {
      const newFilters = new Set(prev);
      if (newFilters.has(status)) {
        newFilters.delete(status);
      } else {
        newFilters.add(status);
      }
      return newFilters;
    });
  };

  const toggleClassFilter = (incidentClass: SaleIncidentClass) => {
    setClassFilters((prev) => {
      const newFilters = new Set(prev);
      if (newFilters.has(incidentClass)) {
        newFilters.delete(incidentClass);
      } else {
        newFilters.add(incidentClass);
      }
      return newFilters;
    });
  };

  return (
    <div className="flex h-full max-h-full w-full min-w-0 flex-col border-r bg-background rounded-lg overflow-hidden">
      <div className="flex flex-col gap-3 border-b px-4 py-4">
        <div className="flex justify-center gap-2 pb-2 w-full">
          <Button
            variant={viewMode === "chats" ? "default" : "outline"}
            size="sm"
            onClick={() => onViewModeChange?.("chats")}
          >
            Chats
          </Button>
          <Button
            variant={viewMode === "incidents" ? "default" : "outline"}
            size="sm"
            onClick={() => onViewModeChange?.("incidents")}
          >
            Incidents
          </Button>
        </div>

        <div className="flex gap-2 -mx-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="flex-1 bg-transparent"
              >
                Class ({classFilters.size})
                <ChevronDown className="ml-auto size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="w-56 max-h-80 overflow-y-auto"
            >
              <DropdownMenuLabel>Filter by class</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {allSaleIncidentClasses.map((incidentClass) => (
                <DropdownMenuCheckboxItem
                  key={incidentClass}
                  checked={classFilters.has(incidentClass)}
                  onCheckedChange={() => toggleClassFilter(incidentClass)}
                >
                  {saleIncidentClassLabels[incidentClass]}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="flex-1 bg-transparent"
              >
                Status ({statusFilters.size})
                <ChevronDown className="ml-auto size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              <DropdownMenuLabel>Filter by status</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuCheckboxItem
                checked={statusFilters.has("open")}
                onCheckedChange={() => toggleStatusFilter("open")}
              >
                Open
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={statusFilters.has("discarded")}
                onCheckedChange={() => toggleStatusFilter("discarded")}
              >
                Discarded
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={statusFilters.has("resolved")}
                onCheckedChange={() => toggleStatusFilter("resolved")}
              >
                Resolved
              </DropdownMenuCheckboxItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="flex-1 bg-transparent"
              >
                Sort: {sortBy === "newest" ? "New" : "Old"}
                <ChevronDown className="ml-auto size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              <DropdownMenuLabel>Sort by</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuRadioGroup
                value={sortBy}
                onValueChange={(v: string) => setSortBy(v as SortOption)}
              >
                <DropdownMenuRadioItem value="newest">
                  Newest First
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="oldest">
                  Oldest First
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto min-h-0 h-0"
      >
        {incidents.length === 0 && !isLoading ? (
          <div className="flex h-full items-center justify-center p-4 text-center text-sm text-muted-foreground">
            No incidents found
          </div>
        ) : (
          <div className="divide-y">
            {incidents.map((incident) => (
              <button
                key={incident.id}
                ref={(el) => {
                  if (el && incident.id) {
                    incidentRefs.current.set(incident.id, el);
                  } else if (incident.id) {
                    incidentRefs.current.delete(incident.id);
                  }
                }}
                onClick={() =>
                  incident.id &&
                  onIncidentSelect?.(
                    incident.id,
                    incident.chatId,
                    incident.date
                  )
                }
                className={cn(
                  "flex w-full min-w-0 flex-col gap-2 p-4 text-left transition-colors",
                  getIncidentBackgroundColor(incident.status),
                  selectedIncidentId === incident.id && "!border-l-4 !border-t-0 !border-b-0 !border-r-0 border-primary"
                )}
              >
                <div className="flex items-start justify-between gap-2 min-w-0">
                  <h3 className={cn(
                    "font-medium text-sm leading-tight truncate min-w-0",
                    incident.status === "discarded" && "line-through"
                  )}>
                    {incident.chatName || "Unknown Chat"}
                  </h3>
                  <span className={cn(
                    "shrink-0 text-xs text-muted-foreground",
                    incident.status === "discarded" && "line-through"
                  )}>
                    {getRelativeTime(incident.unixtime)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span
                    className={cn(
                      "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
                      getClassBadgeColor(incident.class),
                      incident.status === "discarded" && "line-through"
                    )}
                  >
                    {saleIncidentClassLabels[incident.class] || incident.class}
                  </span>
                  <span className={cn(
                    "shrink-0 text-xs font-bold text-gray-700 dark:text-gray-300",
                    incident.status === "discarded" && "line-through"
                  )}>
                    {getStatusLabel(incident.status)}
                  </span>
                </div>
              </button>
            ))}
            {hasMore && (
              <div
                ref={observerTarget}
                className="flex items-center justify-center p-4"
              >
                {isLoading && (
                  <div className="text-sm text-muted-foreground">
                    Loading...
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
